import { useState, useRef, useEffect } from 'react';
import { Mic, Square, Send, RotateCcw, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const MAX_DURATION = 180; // 3 minutes

const VoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorder.current = recorder;
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (animRef.current) cancelAnimationFrame(animRef.current);
      };

      recorder.start(100);
      setIsRecording(true);
      setElapsed(0);
      setAudioBlob(null);
      setAudioUrl(null);

      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);

      drawWaveform();
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone permission.');
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas || !analyserRef.current) return;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!analyserRef.current) return;
      analyser.getByteFrequencyData(dataArray);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        const hue = 270 + (i / bufferLength) * 60;
        ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.9)`;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);
        x += barWidth;
      }
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
    setShowForm(true);
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    setShowForm(false);
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    if (!audioBlob) {
      toast.error('No recording found');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('email', email || '');
      formData.append('audio', audioBlob, 'recording.webm');

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/voice-recordings`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('Voice recording submitted! Our therapist will review it shortly.');
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      toast.error('Failed to submit recording. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="bg-green-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-400/40">
          <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h4 className="text-2xl font-bold text-white mb-3">Recording Submitted</h4>
        <p className="text-purple-200 mb-6">Our therapist will listen to your recording and get back to you soon.</p>
        <Button onClick={resetRecording} variant="outline" className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10" data-testid="record-another-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Record Another
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Waveform Visualizer */}
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={600}
          height={120}
          className="w-full h-24 rounded-xl bg-black/40 border border-purple-400/20"
        />
        {!isRecording && !audioUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-purple-300/60 text-sm">Click record to start describing your health concern</p>
          </div>
        )}
      </div>

      {/* Timer */}
      <div className="flex items-center justify-center gap-3">
        <Clock className="w-4 h-4 text-purple-300" />
        <span className={`text-2xl font-mono font-bold ${isRecording ? 'text-red-400 animate-pulse' : 'text-purple-200'}`}>
          {formatTime(elapsed)}
        </span>
        <span className="text-purple-400 text-sm">/ {formatTime(MAX_DURATION)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        {!isRecording && !audioUrl && (
          <button
            onClick={startRecording}
            className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white w-16 h-16 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ boxShadow: '0 0 30px rgba(239,68,68,0.4)' }}
            data-testid="start-recording-btn"
          >
            <Mic className="w-7 h-7" />
          </button>
        )}

        {isRecording && (
          <button
            onClick={stopRecording}
            className="bg-gradient-to-r from-red-600 to-red-700 text-white w-16 h-16 rounded-full flex items-center justify-center transition-all hover:scale-110 animate-pulse"
            style={{ boxShadow: '0 0 30px rgba(239,68,68,0.6)' }}
            data-testid="stop-recording-btn"
          >
            <Square className="w-6 h-6" />
          </button>
        )}

        {audioUrl && !showForm && (
          <>
            <audio controls src={audioUrl} className="rounded-lg" />
            <Button onClick={resetRecording} variant="outline" className="border-purple-400/40 text-purple-200">
              <RotateCcw className="w-4 h-4 mr-2" /> Re-record
            </Button>
          </>
        )}
      </div>

      {/* Playback + Form after recording */}
      {audioUrl && showForm && (
        <div className="space-y-4 mt-4">
          <audio controls src={audioUrl} className="w-full rounded-lg" data-testid="audio-playback" />

          <div className="grid md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Your Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
              data-testid="recording-name-input"
            />
            <input
              type="tel"
              placeholder="Phone / WhatsApp *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
              data-testid="recording-phone-input"
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
              data-testid="recording-email-input"
            />
          </div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-full"
              data-testid="submit-recording-btn"
            >
              {submitting ? (
                <span className="animate-pulse">Uploading...</span>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit to Therapist</>
              )}
            </Button>
            <Button onClick={resetRecording} variant="outline" className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10 rounded-full">
              <RotateCcw className="w-4 h-4 mr-2" /> Re-record
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
